// ============================================
// Lightweight first-party page-view tracker for vastavik.org
// ============================================
// - No third-party scripts, no cookies.
// - Uses first-party random IDs in localStorage/sessionStorage.
// - Sends a single sendBeacon (text/plain → no CORS preflight) to our API.
// - Completely fire-and-forget; never affects page rendering.
//
// API base resolves from REACT_APP_API_URL (Create React App env),
// falling back to the production API.
// ============================================

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.vastavik.org').replace(/\/+$/, '');
const ENDPOINT = `${API_BASE}/api/v1/analytics/pageview`;

const VID_KEY = 'vstvk_vid';
const SID_KEY = 'vstvk_sid';
const SRC_KEY = 'vstvk_src';

export type TrackedPage = 'verify' | 'creators-portal';

function uid(): string {
    try {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return (crypto as Crypto).randomUUID();
        }
    } catch {
        /* ignore */
    }
    return 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function getVisitorId(): string {
    try {
        let v = localStorage.getItem(VID_KEY);
        if (!v) {
            v = uid();
            localStorage.setItem(VID_KEY, v);
        }
        return v;
    } catch {
        return uid();
    }
}

function getSessionId(): string {
    try {
        let s = sessionStorage.getItem(SID_KEY);
        if (!s) {
            s = uid();
            sessionStorage.setItem(SID_KEY, s);
        }
        return s;
    } catch {
        return uid();
    }
}

// Platform tag from a tagged link (?src=instagram, or ?utm_source=...).
// Persisted for the session so the platform sticks as the visitor navigates
// to pages that don't carry the param.
function getSource(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('src') || params.get('utm_source');
        if (fromUrl) {
            const slug = fromUrl.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
            if (slug) {
                sessionStorage.setItem(SRC_KEY, slug);
                return slug;
            }
        }
        return sessionStorage.getItem(SRC_KEY);
    } catch {
        return null;
    }
}

// Guards against rapid double-fires (e.g. React StrictMode double-mount in dev)
// for the exact same page + path.
let lastSent = '';

export function trackPageView(page: TrackedPage): void {
    if (typeof window === 'undefined') return;

    const path = window.location.pathname + window.location.search;
    const key = page + '|' + path;
    if (key === lastSent) return;
    lastSent = key;

    const payload = {
        page,
        path,
        referrer: document.referrer || null,
        source: getSource(),
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        screenW: window.screen?.width || window.innerWidth || null,
        screenH: window.screen?.height || window.innerHeight || null,
        lang: navigator.language || null,
        ts: Date.now(),
    };

    const body = JSON.stringify(payload);

    // Preferred: sendBeacon (survives navigation/unload, no preflight).
    try {
        const blob = new Blob([body], { type: 'text/plain' });
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
    } catch {
        /* fall through to fetch */
    }

    // Fallback for browsers without a working sendBeacon.
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body,
            keepalive: true,
            mode: 'cors',
        }).catch(() => {
            /* swallow — tracking must never surface errors to the user */
        });
    } catch {
        /* ignore */
    }
}